<script setup lang="ts">
import { formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils/cpf';
import { computed } from 'vue';

import Divider from '~/components/shared/Divider.vue';
import DocumentViewer from '~/components/shared/DocumentViewer.vue';
import InfoGridItem from '~/components/shared/InfoGridItem.vue';

import type { CustomerRegisterApi } from '../../services/customer-register-api/customer-register-api.types';

import useCustomerRegistrationDetails from '../../queries/useCustomerRegistrationDetails';

interface Props {
  isLoading?: boolean;
  projectId: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const registrationQuery = useCustomerRegistrationDetails(computed(() => props.projectId));
const registrationData = computed(() => registrationQuery.data.value);
const internalIsLoading = computed(
  () => props.isLoading || registrationQuery.isLoading.value || registrationQuery.isFetching.value,
);

const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) return null;
  return value ? 'Sim' : 'Não';
};

const hasOtherUnits = computed(() => {
  const electricityBills = registrationData.value?.project?.electricity_bills || [];
  return electricityBills.length > 0;
});

const otherUnitsData = computed(() => {
  const electricityBills = registrationData.value?.project?.electricity_bills || [];
  return electricityBills.map((bill, index) => {
    const electricityBillDocs = bill.documents?.filter((doc) => doc.type === 'electricity_bill') || [];
    const relationshipProofDocs = bill.is_under_customer_name === false
      ? bill.documents?.filter((doc) => doc.type === 'employment_proof') || []
      : [];
    const additionalDocs = bill.documents?.filter((doc) => doc.type === 'additional') || [];
    const comment = (bill as CustomerRegisterApi.ElectricityBill & { comment?: string }).comment || null;

    return {
      additionalDocuments: additionalDocs,
      comment,
      data: [
        { label: 'Conta está no nome do cliente', value: formatBoolean(bill.is_under_customer_name) },
      ].filter((item) => item.value !== null),
      electricityBillDocuments: electricityBillDocs,
      holderCpf: bill.holder_cpf ? formatCpfMasked(normalizeCpfDigits(bill.holder_cpf)) : null,
      index: index + 1,
      relationshipProofDocuments: relationshipProofDocs,
    };
  });
});
</script>

<template>
  <div class="other-units-content">
    <div v-if="!internalIsLoading && !hasOtherUnits" class="empty-state">
      <p class="empty-message">Dados ainda não preenchidos</p>
    </div>

    <template v-else>
      <InfoGridItem
        label="Outras unidades serão beneficiadas/consumidoras desse projeto Solar?"
        value="Sim"
        :is-loading="internalIsLoading"
      />

      <div
        v-for="unit in otherUnitsData"
        :key="unit.index"
        class="unit-section"
      >
        <div class="section-title-wrapper">
          <h5 class="section-title">Unidade {{ unit.index }}</h5>
          <Divider />
        </div>
        <div class="datagrid">
          <InfoGridItem
            v-for="item in unit.data"
            :key="item.label"
            :label="item.label"
            :value="item.value"
            :is-loading="internalIsLoading"
          />
        </div>

        <div v-if="internalIsLoading || unit.electricityBillDocuments.length > 0" class="documents-section">
          <div class="subsection-title-wrapper">
            <h6 class="subsection-title">Conta de Luz da Unidade</h6>
            <Divider />
          </div>
          <div class="documents-list">
            <DocumentViewer
              v-for="(doc, index) in (internalIsLoading ? [undefined] : unit.electricityBillDocuments)"
              :key="internalIsLoading ? `skeleton-${index}` : doc?.id"
              :document="doc"
              :is-loading="internalIsLoading"
            />
          </div>
        </div>

        <div v-if="internalIsLoading || unit.relationshipProofDocuments.length > 0" class="documents-section">
          <div class="subsection-title-wrapper">
            <h6 class="subsection-title">Comprovante de Vínculo</h6>
            <Divider />
          </div>
          <div class="relationship-proof-content">
            <div class="documents-list">
              <DocumentViewer
                v-for="(doc, index) in (internalIsLoading ? [undefined] : unit.relationshipProofDocuments)"
                :key="internalIsLoading ? `skeleton-${index}` : doc?.id"
                :document="doc"
                :is-loading="internalIsLoading"
              />
            </div>
            <div v-if="unit.holderCpf" class="holder-cpf-section">
              <InfoGridItem
                label="CPF do titular da conta"
                :value="unit.holderCpf"
                :is-loading="internalIsLoading"
              />
            </div>
          </div>
        </div>

        <div
          v-if="internalIsLoading || unit.additionalDocuments.length > 0 || unit.comment"
          class="documents-section combined-section"
        >
          <div v-if="internalIsLoading || unit.additionalDocuments.length > 0" class="additional-documents-section">
            <div class="subsection-title-wrapper">
              <h6 class="subsection-title">Documentos Adicionais</h6>
              <Divider />
            </div>
            <div class="documents-list">
              <DocumentViewer
                v-for="(doc, index) in (internalIsLoading ? [undefined] : unit.additionalDocuments)"
                :key="internalIsLoading ? `skeleton-${index}` : doc?.id"
                :document="doc"
                :is-loading="internalIsLoading"
              />
            </div>
          </div>
          <div v-if="unit.comment" class="comment-section">
            <InfoGridItem
              label="Comentário"
              :value="unit.comment"
              :is-loading="internalIsLoading"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.other-units-content {
  .datagrid {
    display: grid;
    gap: 0.75rem;
  }

  .section-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.5rem 0 0.75rem;
    width: 100%;
    min-width: 0;

    &:first-child {
      margin-top: 0;
    }

    :deep(.divider) {
      flex: 1;
      min-width: 0;
      width: auto;
    }
  }

  .section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }

  .subsection-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1rem 0 0.5rem;
    width: 100%;
    min-width: 0;

    :deep(.divider) {
      flex: 1;
      min-width: 0;
      width: auto;
    }
  }

  .subsection-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }

  .unit-section {
    margin-top: 1.5rem;
    padding: 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    background: #f8f9fa;
  }

  .documents-section {
    width: 100%;
    margin-top: 1rem;

    &.combined-section {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 2rem;
    }
  }

  .relationship-proof-content {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
    margin-top: 0.75rem;

    .documents-list {
      margin-top: 0;
      flex: 1;
      min-width: 0;
    }
  }

  .holder-cpf-section {
    flex: 0 0 auto;
    min-width: 250px;
    display: flex;
    align-items: flex-start;
  }

  .comment-section {
    flex: 0 0 auto;
    min-width: 250px;
    display: flex;
    align-items: flex-start;
  }

  .additional-documents-section {
    flex: 1;
    min-width: 0;
  }

  .documents-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
  }

  .empty-message {
    color: #94a3b8;
    font-size: 0.9375rem;
    font-style: italic;
    margin: 0;
  }
}
</style>
